import React, { useState } from 'react';

import {
  ArrowDownward,
  ArrowUpward,
  ContentCopy,
  ContentPaste,
  Delete,
  ExpandLess,
  ExpandMore,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Collapse,
  IconButton,
  Switch,
  TextField,
  Typography,
} from '@mui/material';

import ComponentEditor from './ComponentEditor';
import { availableComponents } from '../../../pages/dynamic-page';
import {
  PageConfig,
  ComponentConfig,
  ComponentStyleProps,
} from '../../../pages/types';
import { ComponentProps } from '../../../App';

interface PageEditorProps {
  pageData: PageConfig;
  onSave: (data: PageConfig) => void;
  configParam: string;
  handleUpload: (blob: Blob, filename: string) => Promise<boolean>;
}

const PageEditor: React.FC<PageEditorProps> = ({
  pageData,
  onSave,
  configParam,
  handleUpload,
}) => {
  const [expandedComponent, setExpandedComponent] = useState<number | null>(
    null
  );
  const [positionInputs, setPositionInputs] = useState<{
    [key: number]: number;
  }>({});
  const [clipboardComponent, setClipboardComponent] =
    useState<ComponentConfig | null>(null);

  const toggleComponentExpand = (index: number) => {
    const wasExpanded = expandedComponent === index;
    setExpandedComponent(wasExpanded ? null : index);

    // If we're expanding a component, scroll to its top
    if (!wasExpanded) {
      // Use setTimeout to wait for the component to expand
      setTimeout(() => {
        const componentElement = document.querySelector(
          `[data-component-index="${index}"]`
        );
        if (componentElement) {
          componentElement.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }
      }, 100);
    }
  };

  const handleComponentChange = (
    index: number,
    updatedProps: ComponentProps,
    updatedStyles: ComponentStyleProps,
    updatedId?: string
  ) => {
    const updatedComponents = [...pageData.components];
    const updatedComponent = {
      ...updatedComponents[index],
      props: updatedProps,
      styles: updatedStyles,
    };

    // Handle ID field
    if (updatedId !== undefined) {
      if (updatedId.trim() === '') {
        delete updatedComponent.id;
      } else {
        updatedComponent.id = updatedId;
      }
    }

    updatedComponents[index] = updatedComponent;
    onSave({ ...pageData, components: updatedComponents });
  };

  const handleIdChange = (index: number, newId: string) => {
    const updatedComponents = [...pageData.components];
    const component = updatedComponents[index];

    if (newId.trim() === '') {
      delete component.id;
    } else {
      component.id = newId;
    }

    updatedComponents[index] = component;
    onSave({ ...pageData, components: updatedComponents });
  };

  const handleAddComponent = (type: string) => {
    const newComponent: ComponentConfig = {
      type,
      props: {},
      styles: {},
    };
    onSave({
      ...pageData,
      components: [...pageData.components, newComponent],
    });
  };

  const handleDeleteComponent = (index: number) => {
    const updatedComponents = pageData.components.filter((_, i) => i !== index);
    onSave({ ...pageData, components: updatedComponents });
  };

  const handleMoveComponent = (index: number, direction: 'up' | 'down') => {
    const updatedComponents = [...pageData.components];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;

    if (targetIndex >= 0 && targetIndex < updatedComponents.length) {
      const [movedComponent] = updatedComponents.splice(index, 1);
      updatedComponents.splice(targetIndex, 0, movedComponent);
      onSave({ ...pageData, components: updatedComponents });
    }
  };

  const handleMoveToPosition = (currentIndex: number, newPosition: number) => {
    // Convert to 0-based index
    const targetIndex = newPosition - 1;

    // Validate position is within bounds
    if (targetIndex < 0 || targetIndex >= pageData.components.length) {
      return; // Invalid position
    }

    // Don't do anything if position is the same
    if (currentIndex === targetIndex) {
      return;
    }

    const updatedComponents = [...pageData.components];
    const [movedComponent] = updatedComponents.splice(currentIndex, 1);
    updatedComponents.splice(targetIndex, 0, movedComponent);

    onSave({ ...pageData, components: updatedComponents });
  };

  const handleDuplicateComponent = (index: number) => {
    const componentToDuplicate = pageData.components[index];
    const duplicatedComponent = {
      ...componentToDuplicate,
      props: { ...componentToDuplicate.props },
      styles: { ...componentToDuplicate.styles },
    };

    const updatedComponents = [...pageData.components];
    updatedComponents.splice(index + 1, 0, duplicatedComponent);

    onSave({ ...pageData, components: updatedComponents });
  };

  const handleCopyComponent = async (index: number) => {
    const componentToCopy = pageData.components[index];
    const copiedComponent = {
      ...componentToCopy,
      props: { ...componentToCopy.props },
      styles: { ...componentToCopy.styles },
    };

    // Store in local state for paste functionality
    setClipboardComponent(copiedComponent);

    // Also copy to system clipboard as JSON for sharing between different pages/sessions
    try {
      const componentJson = JSON.stringify(copiedComponent, null, 2);
      await navigator.clipboard.writeText(componentJson);
      console.log('Component copied to clipboard');
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };

  const handlePasteComponent = async (insertIndex: number) => {
    let componentToPaste = clipboardComponent;

    // If no component in local clipboard, try to get from system clipboard
    if (!componentToPaste) {
      try {
        const clipboardText = await navigator.clipboard.readText();
        componentToPaste = JSON.parse(clipboardText);

        // Validate that it's a component structure
        if (!componentToPaste?.type) {
          throw new Error('Invalid component structure in clipboard');
        }
      } catch (err) {
        console.error('Failed to paste from clipboard:', err);
        return;
      }
    }

    if (componentToPaste) {
      const updatedComponents = [...pageData.components];
      updatedComponents.splice(insertIndex, 0, componentToPaste);
      onSave({ ...pageData, components: updatedComponents });
    }
  };

  const handlePositionChange = (index: number, value: string) => {
    const newPosition = parseInt(value);
    if (!isNaN(newPosition)) {
      setPositionInputs({ ...positionInputs, [index]: newPosition });
    }
  };

  const handlePositionBlur = (index: number) => {
    const newPosition = positionInputs[index];
    if (newPosition !== undefined && !isNaN(newPosition)) {
      handleMoveToPosition(index, newPosition);
      // Reset position inputs after move
      setPositionInputs({});
    }
  };

  return (
    <Box
      sx={{ marginBottom: 10, maxWidth: 1100, textAlign: 'center', mx: 'auto' }}
    >
      <TextField
        label="Menu Title"
        value={pageData.menutitle || ''}
        onChange={(e) => onSave({ ...pageData, menutitle: e.target.value })}
        fullWidth
        margin="normal"
      />
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Typography variant="body1" sx={{ mr: 2 }}>
          Show in Navigation:
        </Typography>
        <Switch
          checked={pageData.showInNavigation !== false} // Default to true if undefined
          onChange={(e) =>
            onSave({ ...pageData, showInNavigation: e.target.checked })
          }
        />
      </Box>
      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {/* Paste button at the top */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<ContentPaste />}
            onClick={() => handlePasteComponent(0)}
            disabled={!clipboardComponent}
            sx={{ opacity: clipboardComponent ? 1 : 0.3 }}
          >
            Paste Component Here
          </Button>
        </Box>

        {pageData.components.map((component, index) => (
          <React.Fragment key={index}>
            <Box data-component-index={index}>
              <Box p={2} sx={{ border: '1px solid #ccc', mb: 2 }}>
                <Box
                  onClick={() => toggleComponentExpand(index)}
                  sx={{
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h5">
                      {component.type || 'Unknown Component'} #{index + 1}
                    </Typography>
                    <TextField
                      label="ID"
                      value={component.id || ''}
                      onChange={(e) => handleIdChange(index, e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      placeholder="e.g. home"
                      size="small"
                      sx={{ width: '120px' }}
                      variant="outlined"
                    />
                  </Box>
                  {expandedComponent === index ? (
                    <ExpandLess />
                  ) : (
                    <ExpandMore />
                  )}
                </Box>
                <Box>
                  <Box display="flex" alignItems="center">
                    <IconButton
                      aria-label="move up"
                      onClick={() => handleMoveComponent(index, 'up')}
                      disabled={index === 0}
                    >
                      <ArrowUpward />
                    </IconButton>
                    <IconButton
                      aria-label="move down"
                      onClick={() => handleMoveComponent(index, 'down')}
                      disabled={index === pageData.components.length - 1}
                    >
                      <ArrowDownward />
                    </IconButton>
                    <TextField
                      type="number"
                      size="small"
                      sx={{ width: '60px', mx: 1 }}
                      value={
                        positionInputs[index] !== undefined
                          ? positionInputs[index]
                          : index + 1
                      }
                      onChange={(e) =>
                        handlePositionChange(index, e.target.value)
                      }
                      onBlur={() => handlePositionBlur(index)}
                      inputProps={{
                        min: 1,
                        max: pageData.components.length,
                      }}
                    />
                    <IconButton
                      aria-label="duplicate"
                      onClick={() => handleDuplicateComponent(index)}
                      title="Duplicate component (insert copy below)"
                    >
                      <ContentCopy />
                    </IconButton>
                    <IconButton
                      aria-label="copy to clipboard"
                      onClick={() => handleCopyComponent(index)}
                      title="Copy to clipboard (for pasting anywhere)"
                      color="secondary"
                    >
                      <ContentCopy style={{ fontSize: '16px' }} />
                    </IconButton>
                    <IconButton
                      aria-label="delete"
                      onClick={() => handleDeleteComponent(index)}
                    >
                      <Delete />
                    </IconButton>
                  </Box>
                </Box>

                <Collapse in={expandedComponent === index}>
                  <ComponentEditor
                    type={component.type}
                    props={component.props || {}}
                    styles={component.styles || {}}
                    onChange={(updatedProps, updatedStyles) =>
                      handleComponentChange(index, updatedProps, updatedStyles)
                    }
                    onDelete={() => handleDeleteComponent(index)}
                    configParam={configParam}
                    handleUpload={handleUpload}
                  />
                </Collapse>
              </Box>
            </Box>

            {/* Paste button after each component */}
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 1 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<ContentPaste />}
                onClick={() => handlePasteComponent(index + 1)}
                disabled={!clipboardComponent}
                sx={{ opacity: clipboardComponent ? 1 : 0.3 }}
              >
                Paste Component Here
              </Button>
            </Box>
          </React.Fragment>
        ))}
      </ul>
      <Box>
        {availableComponents.map((component) => (
          <Button
            key={component.type}
            variant="contained"
            onClick={() => handleAddComponent(component.type)}
          >
            + {component.type}
          </Button>
        ))}
      </Box>
    </Box>
  );
};

export default PageEditor;
